import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Input, Label, Textarea } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast, Toaster } from "sonner";
import { Plus, X, Trash2, Calendar as CalendarIcon, ClipboardCheck, Search } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

const statusMap = {
  planned: { v: "neutral", label: "Planned" },
  in_progress: { v: "info", label: "In progress" },
  review: { v: "warning", label: "Review" },
  completed: { v: "success", label: "Completed" },
  cancelled: { v: "neutral", label: "Cancelled" },
};

const FRAMEWORKS = ["ISO 27001", "SOC 2", "GDPR", "PCI DSS", "HIPAA", "NIST CSF"];

export default function AuditsPageV2() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const empty = { title: "", framework: "ISO 27001", scope: "", objectives: "", methodology: "",
                   lead_auditor_id: "", auditor_ids: [], start_date: "", due_date: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audits");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users?limit=200");
        setUsers(data.items || []);
      } catch (e) {
        setUsers([]);
      }
    })();
  }, []);

  const filtered = items.filter((a) =>
    (!q || a.title.toLowerCase().includes(q.toLowerCase()) || a.framework.toLowerCase().includes(q.toLowerCase())) &&
    (!status || a.status === status)
  );

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (form.start_date) payload.start_date = new Date(form.start_date).toISOString();
      else delete payload.start_date;
      if (form.due_date) payload.due_date = new Date(form.due_date).toISOString();
      else delete payload.due_date;
      if (!payload.lead_auditor_id) delete payload.lead_auditor_id;
      const { data } = await api.post("/audits", payload);
      toast.success("Audit created");
      setShowForm(false);
      setForm(empty);
      navigate(`/audits/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Audits</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Plan, execute and report on compliance audits across frameworks.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="audit-calendar-btn" onClick={() => navigate("/audit-calendar")}>
            <CalendarIcon className="w-4 h-4" /> Calendar
          </Button>
          <Button data-testid="audit-new" onClick={() => { setForm(empty); setShowForm(true); }}>
            <Plus className="w-4 h-4" /> New audit
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="audits-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Search audits…" />
            </div>
            <select data-testid="audits-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
                    className="h-10 w-full sm:w-44 rounded-md border border-border bg-card px-3 text-sm">
              <option value="">All statuses</option>
              {Object.entries(statusMap).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="border-t border-b border-border">
                  <th className="text-left py-2.5 px-6 label-eyebrow">Audit</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Framework</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Status</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Progress</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Due</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Findings</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</td></tr>}
                {!loading && filtered.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No audits found.</td></tr>}
                {filtered.map((a) => {
                  const s = statusMap[a.status] || { v: "neutral", label: a.status };
                  return (
                    <tr key={a.id} data-testid={`audit-row-${a.id}`}
                        onClick={() => navigate(`/audits/${a.id}`)}
                        className="border-b border-border hover:bg-muted/40 transition-colors cursor-pointer">
                      <td className="py-3 px-6 font-medium">{a.title}</td>
                      <td className="py-3 px-6"><Badge variant="primary">{a.framework}</Badge></td>
                      <td className="py-3 px-6"><Badge variant={s.v}>{s.label}</Badge></td>
                      <td className="py-3 px-6">
                        <div className="flex items-center gap-2 w-40">
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden flex-1">
                            <div className={a.progress === 100 ? "h-full bg-green-500" : "h-full bg-primary"} style={{ width: `${a.progress || 0}%` }} />
                          </div>
                          <span className="tabular-nums text-xs w-8 text-right">{a.progress || 0}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-6 tabular-nums text-muted-foreground">{a.due_date ? format(new Date(a.due_date), "MMM d, yyyy") : "—"}</td>
                      <td className="py-3 px-6 text-muted-foreground">—</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto card-surface animate-fade-in" onClick={(e) => e.stopPropagation()} data-testid="audit-form-modal">
            <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card">
              <h3 className="text-base font-semibold">New audit</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Title</Label>
                <Input data-testid="audit-form-title" className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div>
                <Label>Framework</Label>
                <select data-testid="audit-form-framework" value={form.framework}
                        onChange={(e) => setForm({ ...form, framework: e.target.value })}
                        className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm mt-1.5">
                  {FRAMEWORKS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <Label>Lead auditor</Label>
                <select data-testid="audit-form-lead" value={form.lead_auditor_id}
                        onChange={(e) => setForm({ ...form, lead_auditor_id: e.target.value })}
                        className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm mt-1.5">
                  <option value="">Select…</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}
                </select>
              </div>
              <div>
                <Label>Start date</Label>
                <Input type="date" className="mt-1.5" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label>Due date</Label>
                <Input type="date" className="mt-1.5" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Scope</Label>
                <Textarea className="mt-1.5" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Objectives</Label>
                <Textarea className="mt-1.5" value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Methodology</Label>
                <Textarea className="mt-1.5" value={form.methodology} onChange={(e) => setForm({ ...form, methodology: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Additional auditors</Label>
                <select multiple value={form.auditor_ids}
                        onChange={(e) => setForm({ ...form, auditor_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                        className="mt-1.5 w-full rounded-md border border-border bg-card px-3 text-sm min-h-[80px]">
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 sticky bottom-0 bg-card pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" data-testid="audit-form-submit">Create audit</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
